/**
 * Per-face attribute actions for the edit slice — face groups (saved face
 * selections), per-face material assignment, per-face shading override, and
 * the layer-visibility toggles that visualize each.
 *
 * Mirrors the structure of `groupOps.ts`: a single `patchFaceAttributes`
 * pipeline owns undo semantics and the omit-when-empty invariant on
 * `SceneObject.faceAttributes` (via `normalizeFaceAttributes`).
 *
 * `selectedFaces` is the authoritative source in face mode (CLAUDE.md
 * "Never infer face selection from vertex membership"). Every action in
 * this file reads it directly, never re-derives it from vertex membership.
 */

import { topologyFaceCount } from "../../modeling/topology.ts";
import type {
  FaceAttributeTable,
  FaceGroup,
  FaceShadingValue,
} from "../../modeling/types.ts";
import {
  createFaceGroupId,
  FACE_SHADING_AUTO,
  FACE_SHADING_FLAT,
  FACE_SHADING_INHERIT,
  FACE_SHADING_SMOOTH,
  normalizeFaceAttributes,
} from "../../modeling/types.ts";
import { patchSceneObject } from "../sceneObjectMap.ts";
import type { AppState } from "../storeTypes.ts";
import { captureSnapshot, undoManager } from "../storeUndo.ts";
import { computeNormalsForObject, type Get_, type Set_ } from "./core.ts";

// ---------------------------------------------------------------------------
// Naming helpers (private)
// ---------------------------------------------------------------------------

/** Produce a Blender-style unique default name ("FaceGroup", "FaceGroup.001", …). */
function nextDefaultFaceGroupName(groups: readonly FaceGroup[]): string {
  const base = "FaceGroup";
  const used = new Set(groups.map((g) => g.name));
  if (!used.has(base)) return base;
  for (let n = 1; n < 10_000; n++) {
    const name = `${base}.${String(n).padStart(3, "0")}`;
    if (!used.has(name)) return name;
  }
  return `${base}.${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Mutation pipeline (private)
// ---------------------------------------------------------------------------

/**
 * The single mutation path for `SceneObject.faceAttributes`. Captures an
 * undo snapshot, runs `updater` on a fresh copy of the table, then patches
 * the object with the normalized result.
 *
 * Set `recomputeNormals` to `true` for shading-override edits — the renderer
 * picks up new attributes through React deps for free, but `obj.loopNormals`
 * is the persisted bake and must be rebuilt so save/restore round-trips
 * cleanly and other consumers (path tracer, lighting preview) see the
 * correct normals immediately.
 */
function patchFaceAttributes(
  get: Get_,
  set: Set_,
  objectId: string,
  updater: (
    current: FaceAttributeTable | undefined,
  ) => FaceAttributeTable | undefined,
  options?: {
    recomputeNormals?: boolean;
    editModePatch?: (em: AppState["editMode"]) => AppState["editMode"];
  },
): void {
  const obj = get().sceneObjects.get(objectId);
  if (!obj) return;
  undoManager.push(captureSnapshot(get()));
  const next = normalizeFaceAttributes(updater(obj.faceAttributes));
  const state = get();
  let patch: Partial<typeof obj> = { faceAttributes: next };
  if (options?.recomputeNormals) {
    const { loopNormals } = computeNormalsForObject(obj, {
      faceAttributes: next,
    });
    patch = { ...patch, loopNormals };
  }
  set({
    sceneObjects: patchSceneObject(state.sceneObjects, objectId, patch),
    ...(options?.editModePatch
      ? { editMode: options.editModePatch(state.editMode) }
      : {}),
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  });
}

/** Map a UI mode string to its `FaceShadingValue` numeric encoding. */
function shadingModeToValue(
  mode: "inherit" | "flat" | "smooth" | "autoSmooth",
): FaceShadingValue {
  switch (mode) {
    case "flat":
      return FACE_SHADING_FLAT;
    case "smooth":
      return FACE_SHADING_SMOOTH;
    case "autoSmooth":
      return FACE_SHADING_AUTO;
    default:
      return FACE_SHADING_INHERIT;
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createFaceAttrOps(set: Set_, get: Get_) {
  return {
    // ---- Face groups (saved face selections) ----

    createFaceGroup: (objectId: string, name?: string): string | null => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return null;
      const id = createFaceGroupId();
      patchFaceAttributes(
        get,
        set,
        objectId,
        (current) => {
          const groups = current?.groups ?? [];
          const finalName = name?.trim() || nextDefaultFaceGroupName(groups);
          return {
            ...(current ?? {}),
            groups: [
              ...groups,
              { id, name: finalName, faces: new Set<number>() },
            ],
          };
        },
        {
          editModePatch: (em) =>
            em && em.objectId === objectId
              ? { ...em, activeFaceGroupId: id }
              : em,
        },
      );
      return id;
    },

    deleteFaceGroup: (objectId: string, groupId: string) => {
      patchFaceAttributes(
        get,
        set,
        objectId,
        (current) => {
          if (!current?.groups) return current;
          return {
            ...current,
            groups: current.groups.filter((g) => g.id !== groupId),
          };
        },
        {
          editModePatch: (em) =>
            em && em.objectId === objectId && em.activeFaceGroupId === groupId
              ? { ...em, activeFaceGroupId: null }
              : em,
        },
      );
    },

    renameFaceGroup: (objectId: string, groupId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      patchFaceAttributes(get, set, objectId, (current) => {
        if (!current?.groups) return current;
        return {
          ...current,
          groups: current.groups.map((g) =>
            g.id === groupId ? { ...g, name: trimmed } : g,
          ),
        };
      });
    },

    setActiveFaceGroup: (groupId: string | null) => {
      const em = get().editMode;
      if (!em) return;
      if ((em.activeFaceGroupId ?? null) === groupId) return;
      set({ editMode: { ...em, activeFaceGroupId: groupId } });
    },

    assignSelectionToFaceGroup: (objectId: string, groupId: string) => {
      const em = get().editMode;
      if (!em || em.objectId !== objectId) return;
      const sel = em.selectedFaces;
      if (sel.size === 0) return;
      patchFaceAttributes(get, set, objectId, (current) => {
        const groups = current?.groups ?? [];
        return {
          ...(current ?? {}),
          groups: groups.map((g) =>
            g.id === groupId
              ? { ...g, faces: new Set([...g.faces, ...sel]) }
              : g,
          ),
        };
      });
    },

    removeSelectionFromFaceGroup: (objectId: string, groupId: string) => {
      const em = get().editMode;
      if (!em || em.objectId !== objectId) return;
      const sel = em.selectedFaces;
      if (sel.size === 0) return;
      patchFaceAttributes(get, set, objectId, (current) => {
        if (!current?.groups) return current;
        return {
          ...current,
          groups: current.groups.map((g) => {
            if (g.id !== groupId) return g;
            const next = new Set(g.faces);
            for (const f of sel) next.delete(f);
            return { ...g, faces: next };
          }),
        };
      });
    },

    selectFaceGroup: (
      objectId: string,
      groupId: string,
      mode: "replace" | "add" | "subtract",
    ) => {
      const obj = get().sceneObjects.get(objectId);
      const em = get().editMode;
      if (!obj || !em || em.objectId !== objectId) return;
      // Face groups don't apply to curves — they have no faces.
      if (em.isCurve) return;
      const group = obj.faceAttributes?.groups?.find((g) => g.id === groupId);
      if (!group) return;
      // Compute next face selection.
      let nextFaces: Set<number>;
      if (mode === "replace") {
        nextFaces = new Set(group.faces);
      } else if (mode === "add") {
        nextFaces = new Set(em.selectedFaces);
        for (const f of group.faces) nextFaces.add(f);
      } else {
        nextFaces = new Set(em.selectedFaces);
        for (const f of group.faces) nextFaces.delete(f);
      }
      // In face mode, face selection is authoritative; derive vertices from
      // it. In other modes, set selectedFaces but leave vertex/edge selection
      // empty (the user explicitly recalled a face group while in
      // vertex/edge mode — flipping to face mode is up to them).
      if (em.granularity === "face") {
        const verts = new Set<number>();
        const { offsets, corners } = obj.topology;
        for (const f of nextFaces) {
          const fs = offsets[f];
          const fe = offsets[f + 1];
          for (let c = fs; c < fe; c++) verts.add(corners[c]);
        }
        set({
          editMode: {
            ...em,
            selectedFaces: nextFaces,
            selectedVertices: verts,
            selectedEdges: new Set(),
            activeFaceGroupId: groupId,
          },
        });
        return;
      }
      set({
        editMode: {
          ...em,
          selectedFaces: nextFaces,
          activeFaceGroupId: groupId,
        },
      });
    },

    // ---- Per-face material assignment ----

    assignFacesToMaterialSlot: (objectId: string, slotIndex: number) => {
      const obj = get().sceneObjects.get(objectId);
      const em = get().editMode;
      if (!obj || !em || em.objectId !== objectId) return;
      const sel = em.selectedFaces;
      if (sel.size === 0) return;
      if (slotIndex < 0 || slotIndex >= obj.materialSlots.length) return;
      const faceCount = topologyFaceCount(obj.topology);
      if (faceCount === 0) return;

      patchFaceAttributes(get, set, objectId, (current) => {
        // Materialize the per-face index array. Existing data is used as the
        // starting point; otherwise allocate a fresh zero-filled array (every
        // face starts on slot 0).
        const out = current?.materialIndex
          ? new Uint16Array(current.materialIndex)
          : new Uint16Array(faceCount);
        // Defensive: if existing array length doesn't match faceCount (e.g.
        // a topology op left it shorter), expand to faceCount with zeros.
        let working = out;
        if (working.length !== faceCount) {
          working = new Uint16Array(faceCount);
          working.set(out.subarray(0, Math.min(out.length, faceCount)));
        }
        for (const f of sel) {
          if (f >= 0 && f < faceCount) working[f] = slotIndex;
        }
        return { ...(current ?? {}), materialIndex: working };
      });
    },

    /**
     * Assign every face in the named face group to a material slot. Bypasses
     * `selectedFaces` so the user doesn't need to enter face mode and select
     * the group first — letting object-mode users drive material assignment
     * from the saved face-group list directly.
     */
    assignFaceGroupToMaterialSlot: (
      objectId: string,
      slotIndex: number,
      faceGroupId: string,
    ) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      if (slotIndex < 0 || slotIndex >= obj.materialSlots.length) return;
      const group = obj.faceAttributes?.groups?.find(
        (g) => g.id === faceGroupId,
      );
      if (!group || group.faces.size === 0) return;
      const faceCount = topologyFaceCount(obj.topology);
      if (faceCount === 0) return;

      patchFaceAttributes(get, set, objectId, (current) => {
        const out = current?.materialIndex
          ? new Uint16Array(current.materialIndex)
          : new Uint16Array(faceCount);
        let working = out;
        if (working.length !== faceCount) {
          working = new Uint16Array(faceCount);
          working.set(out.subarray(0, Math.min(out.length, faceCount)));
        }
        for (const f of group.faces) {
          if (f >= 0 && f < faceCount) working[f] = slotIndex;
        }
        return { ...(current ?? {}), materialIndex: working };
      });
    },

    selectFacesByMaterialSlot: (
      objectId: string,
      slotIndex: number,
      mode: "replace" | "add" | "subtract",
    ) => {
      const obj = get().sceneObjects.get(objectId);
      const em = get().editMode;
      if (!obj || !em || em.objectId !== objectId) return;
      if (em.isCurve) return;
      const faceCount = topologyFaceCount(obj.topology);
      if (faceCount === 0) return;
      const stored = obj.faceAttributes?.materialIndex;

      // Determine which faces use this slot. Slot 0 is implicit (faces with
      // no per-face entry use slot 0), so when targeting slot 0 we include
      // faces beyond the stored array's length.
      const matched = new Set<number>();
      for (let f = 0; f < faceCount; f++) {
        const v = stored && f < stored.length ? stored[f] : 0;
        if (v === slotIndex) matched.add(f);
      }

      let nextFaces: Set<number>;
      if (mode === "replace") {
        nextFaces = matched;
      } else if (mode === "add") {
        nextFaces = new Set(em.selectedFaces);
        for (const f of matched) nextFaces.add(f);
      } else {
        nextFaces = new Set(em.selectedFaces);
        for (const f of matched) nextFaces.delete(f);
      }

      if (em.granularity === "face") {
        const verts = new Set<number>();
        const { offsets, corners } = obj.topology;
        for (const f of nextFaces) {
          const fs = offsets[f];
          const fe = offsets[f + 1];
          for (let c = fs; c < fe; c++) verts.add(corners[c]);
        }
        set({
          editMode: {
            ...em,
            selectedFaces: nextFaces,
            selectedVertices: verts,
            selectedEdges: new Set(),
          },
        });
        return;
      }
      set({ editMode: { ...em, selectedFaces: nextFaces } });
    },

    // ---- Per-face shading override ----

    setFaceShading: (
      objectId: string,
      mode: "inherit" | "flat" | "smooth" | "autoSmooth",
    ) => {
      const obj = get().sceneObjects.get(objectId);
      const em = get().editMode;
      if (!obj || !em || em.objectId !== objectId) return;
      const sel = em.selectedFaces;
      if (sel.size === 0) return;
      const faceCount = topologyFaceCount(obj.topology);
      if (faceCount === 0) return;
      const value = shadingModeToValue(mode);

      patchFaceAttributes(
        get,
        set,
        objectId,
        (current) => {
          const out = current?.shading
            ? new Uint8Array(current.shading)
            : new Uint8Array(faceCount);
          let working = out;
          if (working.length !== faceCount) {
            working = new Uint8Array(faceCount);
            working.set(out.subarray(0, Math.min(out.length, faceCount)));
          }
          for (const f of sel) {
            if (f >= 0 && f < faceCount) working[f] = value;
          }
          return { ...(current ?? {}), shading: working };
        },
        // Per-face shading affects normals; recompute on the patched object so
        // the persisted bake stays consistent.
        { recomputeNormals: true },
      );
    },

    // ---- Display-only color overlay toggles ----

    setShowFaceGroupColors: (value: boolean) => {
      const em = get().editMode;
      if (!em) return;
      set({ editMode: { ...em, showFaceGroupColors: value } });
    },

    setShowMaterialSlotColors: (value: boolean) => {
      const em = get().editMode;
      if (!em) return;
      set({ editMode: { ...em, showMaterialSlotColors: value } });
    },
  };
}
